import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@opendoor/bricks-next';
import { motion } from 'framer-motion';

import { trapFocus } from '../trapFocus';
import type { MenuConfig, NavigationProps } from '../types';
import { MenuContent } from './components/MenuContent';
import * as menuAnimations from './menuAnimations';
import { backdropVariants } from './menuAnimations';

interface MenuPanelProps extends NavigationProps, MenuConfig {
  isVisible?: boolean;
  setIsVisible: (isVisible: boolean) => void;
  exitComplete: () => void;
  variant?: 'default' | 'glass';
}

const Menu = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  isVisible,
  setIsVisible,
  exitComplete,
  megaNavData,
  variant = 'default',
}: MenuPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animate = isVisible ? 'visible' : 'hidden';

  const onComplete = (definition: 'visible' | 'hidden') => {
    if (definition === 'visible') {
      containerRef.current?.focus();
    } else {
      exitComplete();
    }
  };

  return (
    <motion.div
      variants={menuAnimations.menuVariants}
      animate={animate}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => trapFocus(e, containerRef)}
      style={{
        position: 'fixed',
        inset: variant === 'glass' ? '72px 0 0 0' : '0',
        zIndex: 1000,
      }}
    >
      <Box $platform-web={{ display: 'grid' }} height="100%" width="100%">
        <MenuContent
          isVisible={!!isVisible}
          containerRef={containerRef}
          onAnimationComplete={onComplete}
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaLink={ctaLink}
          ctaTitle={ctaTitle}
          megaNavData={megaNavData}
          variant={variant}
        />
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          exit="hidden"
          aria-hidden="true"
          onClick={() => setIsVisible(false)}
          style={{
            position: 'fixed',
            inset: variant === 'glass' ? '72px 0 0 0' : '0',
            zIndex: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
          }}
        />
      </Box>
    </motion.div>
  );
};

const MenuPanel = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  isVisible,
  setIsVisible,
  exitComplete,
  megaNavData,
  variant = 'default',
}: MenuPanelProps) => {
  const [mounted, setMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(Boolean(isVisible));
  const isVisibleRef = useRef(Boolean(isVisible));
  isVisibleRef.current = Boolean(isVisible);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    }
  }, [isVisible]);

  useEffect(() => {
    document.body.classList.toggle('has-sheet', isVisible);
    return () => document.body.classList.remove('has-sheet');
  }, [isVisible]);

  const escFunction = useCallback((event: { key: string }) => {
    if (event.key === 'Escape') {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const handleExitComplete = useCallback(() => {
    if (!isVisibleRef.current) {
      setShouldRender(false);
    }
    exitComplete();
  }, [exitComplete]);

  return mounted && shouldRender ? (
    <Menu
      slug={slug}
      menuPanelNavLinks={menuPanelNavLinks}
      mainNavLinks={mainNavLinks}
      ctaLink={ctaLink}
      ctaTitle={ctaTitle}
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      exitComplete={handleExitComplete}
      megaNavData={megaNavData}
      variant={variant}
    />
  ) : null;
};

export default MenuPanel;
